import { useEffect, useState, Suspense } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  LayoutDashboard,
  LogOut,
  Lock,
  Workflow,
  Building2,
  ChevronsUpDown,
  Plus,
  Check,
  Smartphone,
  Inbox,
  Users,
  Handshake,
  CreditCard,
  Bell,
  User,
  Sparkles,
  LifeBuoy,
  Headset,
  Menu,
  Kanban,
  ChartNoAxesCombinedIcon,
  Database,
  Waypoints,
  Shield,
  ScrollText,
  FileCheck2,
  Activity,
  Megaphone,
  ThumbsDown,
  CalendarClock,
  Circle,
  Coffee,
  Search,
  GraduationCap,
} from "lucide-react";

import { ActiveCallBar } from "@/components/ActiveCallBar";
import { PendingCallBanner } from "@/components/PendingCallBanner";
import TermsAcceptanceGate from "@/components/TermsAcceptanceGate";
import { TermsGateProvider } from "@/components/legal/TermsGateProvider";
import BrandLoader from "@/components/BrandLoader";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
  SidebarLock,
  SidebarToggle,
  useSidebar,
} from "@/components/internal/Sidebar";
import { NotificationBell } from "@/components/NotificationBell";
import useAccountSocketEvents from "@/hooks/useAccountSocketEvents";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { apiGet } from "@/lib/api";
import { accountPath, billingPath, embedPath, PLATFORM_ROUTES } from "@/utils/accountRoutes";
import { matchesCompanyQuery } from "@/utils/companyFilter";
import BlockedAccountOverlay from "@/components/BlockedAccountOverlay";
import SeatBlockDialog from "@/components/SeatBlockDialog";
import WhatsAppNumberUpgradeDialog from "@/components/WhatsAppNumberUpgradeDialog";
import { ThemeToggleSimple } from "@/components/ThemeToggle";
import { useSupportChatUI } from "@/contexts/SupportChatContext";
import {
  MAIN_NAV as SHARED_MAIN_NAV,
  CORE_NAV as SHARED_CORE_NAV,
  TEAM_NAV as SHARED_TEAM_NAV,
  CONFIG_NAV as SHARED_CONFIG_NAV,
  filterNavForEmbed,
  filterNavByPartner,
  filterNavByProgram,
} from "@touchworks/shared/platform/navigation.js";
import { useProgramMembership } from "@/hooks/useProgramMembership";
import { withIcons } from "@/components/nav/navIcons.jsx";

// ═══════════════════════════════════════════════════════════════
// ROLE-BASED NAVIGATION
// minLevel: nível mínimo de hierarquia para ver o item
//   100 = Super Admin | 50 = Admin | 30 = Supervisor | 10 = Agent | 0 = Viewer
// ═══════════════════════════════════════════════════════════════

// A navegação vem do catálogo compartilhado: o Lucas ensina o caminho até
// estes itens, e um rótulo escrito à mão do lado dele envelhece em silêncio.
// A resolução do ícone mora em `navIcons`, compartilhada com o cartão de
// caminho que ele mostra — ícone diferente faria a pessoa procurar outra coisa.
const MAIN_NAV = withIcons(SHARED_MAIN_NAV);
const CORE_NAV = withIcons(SHARED_CORE_NAV);
const TEAM_NAV = withIcons(SHARED_TEAM_NAV);
const CONFIG_NAV = withIcons(SHARED_CONFIG_NAV);

/**
 * As rotas `/internal${path}` de todo item `flat` do catálogo — usado só para
 * o efeito que deriva `selectedCompanyId` da URL não confundir "estou numa
 * tela sem :clientId" com "saí da conta que eu estava vendo". Derivado do
 * catálogo, não escrito à mão: um item `flat` novo já cai aqui sozinho, sem
 * precisar lembrar de atualizar este efeito de novo.
 */
const FLAT_NAV_PATHS = [...MAIN_NAV, ...CORE_NAV, ...TEAM_NAV, ...CONFIG_NAV]
  .filter((item) => item.flat)
  .map((item) => `/internal${item.path}`);

/**
 * Filtra itens de navegação baseado no nível do usuário
 * SuperAdmins (isSuperAdmin) veem tudo independente do level
 */
function filterNavByLevel(items, userLevel, isSuper) {
  if (isSuper) return items;
  return items.filter((item) => (item.minLevel ?? 0) <= userLevel);
}

/**
 * Filtra itens de navegação baseado em features do plano
 * Remove itens cuja feature não está disponível no plano
 * Trial ativo e SuperAdmin veem tudo
 */
function filterNavByFeature(items, hasFeature, isTrial, isSuper) {
  if (isSuper || isTrial) return items;
  return items.filter((item) => !item.feature || hasFeature(item.feature));
}

/**
 * Filtra itens de navegação baseado em permissões granulares
 * Remove itens cuja permission o usuário não possui
 * SuperAdmins veem tudo independente de permissão
 */
function filterNavByPermission(items, hasPermission, isSuper) {
  if (isSuper) return items;
  return items.filter(
    (item) => !item.permission || hasPermission?.(item.permission),
  );
}

/**
 * Compõe TODOS os eixos de visibilidade num só lugar — nível, feature,
 * permissão, (EMB-13) embed e (PCA-11) conta parceira. Os filtros viviam
 * espalhados em até duas chamadas por lista (uma para o `.length > 0`, outra
 * para o `.map`); um item novo que devesse ficar fora do shell embutido tinha
 * até 6 lugares pra esquecer de excluir. Um só ponto de composição resolve
 * isso pela raiz.
 *
 * `filterNavByPartner` é o ÚNICO eixo que não recebe `isSuper`, de propósito:
 * o superadmin aqui está olhando o menu DA CONTA, e um item que ele veja numa
 * conta não-parceira dá 403 ao ser clicado (o endpoint também não tem bypass
 * de superadmin — design.md, Tech Decisions).
 */
function visibleNavItems(items, { maxRoleLevel, isSuper, hasFeature, isTrial, hasPermission, isEmbed, isPartnerAccount, isInProgram }) {
  const byRule = filterNavByProgram(filterNavByPartner(
    filterNavByPermission(
      filterNavByFeature(
        filterNavByLevel(items, maxRoleLevel, isSuper),
        hasFeature,
        isTrial,
        isSuper,
      ),
      hasPermission,
      isSuper,
    ),
    isPartnerAccount,
  ), isInProgram);
  return isEmbed ? filterNavForEmbed(byRule) : byRule;
}

/**
 * O portão de aceite embrulha o layout inteiro — inclusive o <Outlet /> — porque
 * quem depende dele não é só o modal: os tours guiados renderizados pelas
 * páginas filhas leem o mesmo status para não abrirem por cima do aceite.
 * Fica aqui, e não em cada rota do App, para que todo ponto de montagem do
 * layout (interno e embed) herde o portão sem precisar lembrar dele.
 */
export default function InternalLayout() {
  return (
    <TermsGateProvider>
      <InternalLayoutShell />
    </TermsGateProvider>
  );
}

function InternalLayoutShell() {
  // Notificações globais de conta/update para admin/supervisor
  useAccountSocketEvents();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    logout,
    isAuthenticated,
    isSuperAdmin,
    switchAccount,
    maxRoleLevel,
    hasMinLevel,
    hasPermission,
    updatePresenceStatus,
    hasFeature,
    isTrial,
    features,
    planSlug,
    isPartnerAccount,
  } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [myAccounts, setMyAccounts] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [companyFilter, setCompanyFilter] = useState("");
  const lucasChat = useSupportChatUI();

  const isSuper = isSuperAdmin();
  const canAccessBilling =
    maxRoleLevel >= 50 || hasPermission?.("billing.read");

  // EMB-13: a rota é o único sinal — decidir aqui em vez de depender de
  // EmbedContext (T16 ainda vai posicioná-lo na árvore de providers) mantém
  // este layout auto-suficiente e testável isoladamente.
  const isEmbed = location.pathname.startsWith("/embed");

  // Programa "Arquitetura de Operação": o item aparece por participação na turma.
  const { isInProgram } = useProgramMembership({ enabled: isAuthenticated && !isEmbed });
  const navFilterCtx = { maxRoleLevel, isSuper, hasFeature, isTrial, hasPermission, isEmbed, isPartnerAccount, isInProgram };
  const mainNavItems = visibleNavItems(MAIN_NAV, navFilterCtx);
  const coreNavItems = visibleNavItems(CORE_NAV, navFilterCtx);
  const teamNavItems = visibleNavItems(TEAM_NAV, navFilterCtx);
  const configNavItems = visibleNavItems(CONFIG_NAV, navFilterCtx);

  // EMB-11: no shell embutido a URL não carrega :clientId (a conta vem do
  // bearer da sessão embutida) — todo link de navegação da conta passa por
  // aqui em vez de montar `/internal/implementation/${selectedCompanyId}...`
  // direto, para nunca esquecer o caso embed em um novo lugar.
  const companyPath = (subPath = "") =>
    isEmbed ? embedPath(subPath) : `/internal/implementation/${selectedCompanyId}${subPath}`;

  // Itens `flat` (ex.: Indique e Ganhe) moram fora de `implementation/:clientId`
  // — a rota é a mesma pra qualquer conta que a sessão logada representar, não
  // pra qualquer conta que um superadmin esteja navegando por `:clientId`.
  const navItemPath = (item) => (item.flat ? `/internal${item.path}` : companyPath(item.path));

  // "Upgrade to Pro" — só mostra para admin 50+ quando TODOS os limites
  // numéricos principais estiverem esgotados (remaining ≤ 0)
  const shouldShowUpgrade = (() => {
    if (isSuper || isTrial || maxRoleLevel < 50) return false;
    if (planSlug === "enterprise") return false; // já no plano máximo
    if (!features || Object.keys(features).length === 0) return false;

    // Limites-chave que indicam capacidade da conta
    const KEY_LIMITS = [
      "users.max",
      "users.agents_max",
      "flows.max",
      "inbox.tickets_monthly",
    ];
    const applicable = KEY_LIMITS.filter((k) => {
      const f = features[k];
      return f && f.limitType === "number" && f.limitValue > 0;
    });
    if (applicable.length === 0) return false; // sem limites numéricos → sem upgrade

    return applicable.every((k) => {
      const f = features[k];
      const remaining = f.remaining ?? f.limitValue - (f.used || 0);
      return remaining <= 0;
    });
  })();

  // For non-superadmins: auto-select their own account and redirect if needed
  useEffect(() => {
    if (isAuthenticated && user && !isSuper && user.accountId) {
      setSelectedCompanyId(user.accountId);
      // If they're on the global dashboard, redirect to their implementation
      if (
        location.pathname === "/internal" ||
        location.pathname === "/internal/" ||
        location.pathname === "/internal/dashboard"
      ) {
        navigate(`/internal/implementation/${user.accountId}`, {
          replace: true,
        });
      }
    }
  }, [isAuthenticated, user, isSuper]);

  // Fetch companies on mount (only for superadmins)
  useEffect(() => {
    if (isAuthenticated && isSuper) {
      fetchCompanies();
    }
  }, [isAuthenticated, isSuper]);

  // Fetch user's available accounts (for non-superadmins with multi-account)
  useEffect(() => {
    if (isAuthenticated && !isSuper) {
      fetchMyAccounts();
    }
  }, [isAuthenticated, isSuper]);

  // Extract company ID from URL to sync state
  useEffect(() => {
    // Non-superadmins always keep their own accountId selected
    if (!isSuper && user?.accountId) {
      setSelectedCompanyId(user.accountId);
      return;
    }

    const pathParts = location.pathname.split("/");
    const searchParams = new URLSearchParams(location.search);
    const queryCompanyId = searchParams.get("companyId");

    // Support both legacy /internal/companies/:id and new /implementation/:id
    if (
      location.pathname.includes("/implementation/") &&
      pathParts.length > 3
    ) {
      setSelectedCompanyId(pathParts[3]);
    } else if (
      location.pathname.includes("/companies/") &&
      pathParts.length > 3
    ) {
      setSelectedCompanyId(pathParts[3]);
    } else if (queryCompanyId) {
      // Support companyId in query string
      setSelectedCompanyId(queryCompanyId);
    } else if (FLAT_NAV_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      // Uma rota flat (ex.: Indique e Ganhe) não carrega :clientId nenhum —
      // mas isso não quer dizer que o superadmin "saiu" da conta que estava
      // olhando. Sem este caso, clicar no item resetava o seletor de contas
      // pra "todas as contas" no meio da navegação (achado ao vivo pelo
      // Danilo): a tela em si sempre foi da conta da SESSÃO mesmo, então só
      // preservamos o que já estava selecionado — nada pra derivar daqui.
    } else {
      setSelectedCompanyId(null);
    }
  }, [location.pathname, location.search, isSuper, user]);

  // Auto-switch account when superadmin navigates to a company URL
  // This ensures the JWT cookie matches the company being viewed
  useEffect(() => {
    if (!isSuper || !selectedCompanyId || isSwitching) return;
    // If the user's current accountId doesn't match the selected company, switch
    if (user?.accountId !== selectedCompanyId) {
      console.log(
        "[InternalLayout] Auto-switching account to match URL:",
        selectedCompanyId,
        "(current:",
        user?.accountId,
        ")",
      );
      setIsSwitching(true);
      switchAccount(selectedCompanyId)
        .then(() => console.log("[InternalLayout] Auto-switch complete"))
        .catch((err) =>
          console.error("[InternalLayout] Auto-switch failed:", err),
        )
        .finally(() => setIsSwitching(false));
    }
  }, [selectedCompanyId, isSuper, user?.accountId]);

  // Content is ready when not actively switching accounts
  const isAccountReady =
    !isSwitching &&
    (!isSuper
      ? user?.accountId === selectedCompanyId // non-superadmin: account must match
      : !selectedCompanyId || user?.accountId === selectedCompanyId); // superadmin

  const fetchCompanies = async () => {
    try {
      const res = await fetch("/api/internal/companies", {
        credentials: "include", // Use HttpOnly cookie for auth
      });
      if (res.ok) {
        const data = await res.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error("Failed to fetch companies", error);
    }
  };

  const fetchMyAccounts = async () => {
    try {
      const res = await apiGet("/api/auth/my-accounts");
      if (res.ok) {
        const data = await res.json();
        setMyAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error("Failed to fetch my accounts", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Compute current path base for navigation
  const basePath = "/internal";

  const handleCompanyChange = async (companyId) => {
    console.log("[InternalLayout] handleCompanyChange called with:", companyId);

    if (isSwitching) return;

    if (companyId === "global") {
      // Switch back to own account
      if (user?.originalAccountId) {
        setIsSwitching(true);
        try {
          await switchAccount("global");
          await fetchCompanies(); // Refresh companies list
        } catch (error) {
          console.error(
            "[InternalLayout] Failed to switch back to own account:",
            error,
          );
        } finally {
          setIsSwitching(false);
        }
      }
      navigate(`${basePath}/dashboard`);
    } else if (companyId === "new") {
      navigate(`${basePath}/dashboard`);
    } else {
      // Switch to target company account
      setIsSwitching(true);
      try {
        await switchAccount(companyId);
        await fetchCompanies(); // Refresh companies list
        console.log(
          "[InternalLayout] Navigating to:",
          `${basePath}/implementation/${companyId}`,
        );
        navigate(`${basePath}/implementation/${companyId}`);
      } catch (error) {
        console.error("[InternalLayout] Failed to switch account:", error);
        // Still navigate even if switch fails (fallback)
        navigate(`${basePath}/implementation/${companyId}`);
      } finally {
        setIsSwitching(false);
      }
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.fullName) {
      return user.fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return "TW";
  };

  const currentCompany = companies.find((c) => c.id === selectedCompanyId);

  /**
   * A home é desenho full-bleed: a aurora vai até a borda e a tela do Lucas
   * ocupa a viewport inteira. O padding do ramo sem conta selecionada a
   * emoldura — invisível enquanto a página rolava, evidente depois que a tela
   * do Lucas passou a travar a altura.
   *
   * `selectedCompanyId` começa nulo e só é preenchido pelo seletor, então
   * abrir a URL da conta direto cai no ramo com padding mesmo tendo conta.
   */
  const isFullBleedRoute =
    /^\/internal\/implementation(\/[^/]+)?\/?$/.test(location.pathname) ||
    // Quadro ao vivo do programa: ocupa a tela toda ao lado da chamada de vídeo.
    /^\/internal\/programa\/quadro\/[^/]+\/?$/.test(location.pathname);

  // For non-superadmins, derive company name from user data or myAccounts
  const currentAccount = myAccounts.find((a) => a.isCurrent);
  const ownCompanyName =
    currentAccount?.companyName || user?.companyName || "Minha Empresa";
  const hasMultipleAccounts = myAccounts.length > 1;

  // Handle non-superadmin account switch
  const handleAccountSwitch = async (accountId) => {
    if (isSwitching || accountId === user?.accountId) return;
    setIsSwitching(true);
    try {
      await switchAccount(accountId);
      await fetchMyAccounts();
      navigate(`/internal/implementation/${accountId}`, { replace: true });
    } catch (error) {
      console.error("[InternalLayout] Failed to switch account:", error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="flex h-screen bg-background text-foreground-strong font-sans overflow-hidden">
      <ActiveCallBar />
      <PendingCallBanner />
      <TermsAcceptanceGate />
      <BlockedAccountOverlay />
      <SeatBlockDialog />
      <WhatsAppNumberUpgradeDialog />
      <Sidebar
        forceCollapsed
        mobileHeader={
          <MobileHeader
            currentCompany={
              isSuper ? currentCompany : { companyName: ownCompanyName }
            }
          />
        }
      >
        {/* Header: Account Switcher (superadmin) or Static Company (regular admin) */}
        <SidebarHeader className="bg-background/50 backdrop-blur-sm sticky top-0 z-10 h-auto py-4 border-b border-border">
          <div className="w-full px-2 group-data-[collapsed=true]:px-0 transition-all">
            {isSuper ? (
              /* ═══ SUPERADMIN: Full Account Switcher ═══ */
              <DropdownMenu
                onOpenChange={(open) => {
                  if (!open) setCompanyFilter("");
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between gap-2 p-2 rounded-lg bg-surface border border-border hover:bg-surface-hover hover:border-border-strong transition-all group-data-[collapsed=true]:w-10 group-data-[collapsed=true]:h-10 group-data-[collapsed=true]:mx-auto group-data-[collapsed=true]:justify-center group-data-[collapsed=true]:p-0 group-data-[collapsed=true]:bg-transparent group-data-[collapsed=true]:border-transparent hover:group-data-[collapsed=true]:bg-surface-hover">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-border transition-colors ${currentCompany ? "bg-surface-active text-foreground-strong" : "bg-surface text-foreground-muted"}`}
                      >
                        {currentCompany ? (
                          <span className="font-bold text-xs">
                            {currentCompany.companyName.charAt(0)}
                          </span>
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex flex-col items-start overflow-hidden text-left group-data-[collapsed=true]:hidden">
                        <span className="font-medium text-sm truncate w-full text-foreground group-hover:text-foreground-strong transition-colors">
                          {currentCompany
                            ? currentCompany.companyName
                            : "Touchworks"}
                        </span>
                        <span className="text-[10px] text-foreground-muted truncate w-full group-hover:text-foreground-muted">
                          {currentCompany ? "Cliente" : "Admin Global"}
                        </span>
                      </div>
                    </div>
                    <ChevronsUpDown className="w-4 h-4 text-foreground-muted shrink-0 group-data-[collapsed=true]:hidden" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-60 bg-surface border-border text-foreground z-[100]">
                  <DropdownMenuLabel className="text-xs text-foreground-muted font-normal uppercase tracking-wider">
                    Conta
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer focus:bg-surface-hover focus:text-foreground-strong"
                    onSelect={() => handleCompanyChange("global")}
                  >
                    <div className="w-6 h-6 rounded bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">Touchworks</span>
                      <span className="text-[10px] text-foreground-muted">
                        Admin Global
                      </span>
                    </div>
                    {!selectedCompanyId && (
                      <Check className="ml-auto w-4 h-4 text-cyan-500" />
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuLabel className="text-xs text-foreground-muted font-normal uppercase tracking-wider">
                    Clientes
                  </DropdownMenuLabel>

                  {/* Search / Filter */}
                  <div className="px-2 pb-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                      <input
                        autoFocus
                        placeholder="Buscar por nome ou CNPJ…"
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  </div>

                  <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                    {companies
                      .filter((c) => matchesCompanyQuery(c, companyFilter))
                      .map((company) => (
                        <DropdownMenuItem
                          key={company.id}
                          className="gap-2 cursor-pointer focus:bg-surface-hover focus:text-foreground-strong"
                          onSelect={() => handleCompanyChange(company.id)}
                        >
                          <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-white uppercase leading-none">
                              {company.companyName.charAt(0)}
                            </span>
                          </div>
                          <span className="truncate">
                            {company.companyName}
                          </span>
                          {selectedCompanyId === company.id && (
                            <Check className="ml-auto w-4 h-4 text-cyan-500" />
                          )}
                        </DropdownMenuItem>
                      ))}
                  </div>

                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer focus:bg-surface-hover focus:text-foreground-strong"
                    onSelect={() => navigate(`${basePath}/dashboard`)}
                  >
                    <div className="w-6 h-6 rounded border border-dashed border-border-strong flex items-center justify-center">
                      <Plus className="w-3 h-3 text-foreground-muted" />
                    </div>
                    <span>Adicionar Cliente</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : hasMultipleAccounts ? (
              /* ═══ REGULAR USER WITH MULTIPLE ACCOUNTS: Account Switcher ═══ */
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between gap-2 p-2 rounded-lg bg-surface border border-border hover:bg-surface-hover hover:border-border-strong transition-all group-data-[collapsed=true]:w-10 group-data-[collapsed=true]:h-10 group-data-[collapsed=true]:mx-auto group-data-[collapsed=true]:justify-center group-data-[collapsed=true]:p-0 group-data-[collapsed=true]:bg-transparent group-data-[collapsed=true]:border-transparent hover:group-data-[collapsed=true]:bg-surface-hover">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-border bg-surface-active text-foreground-strong">
                        <span className="font-bold text-xs">
                          {ownCompanyName.charAt(0)}
                        </span>
                      </div>
                      <div className="flex flex-col items-start overflow-hidden text-left group-data-[collapsed=true]:hidden">
                        <span className="font-medium text-sm truncate w-full text-foreground group-hover:text-foreground-strong transition-colors">
                          {ownCompanyName}
                        </span>
                        <span className="text-[10px] text-foreground-muted truncate w-full group-hover:text-foreground-muted">
                          {currentAccount?.role === "ROLE_ADMIN"
                            ? "Administrador"
                            : "Agente"}
                        </span>
                      </div>
                    </div>
                    <ChevronsUpDown className="w-4 h-4 text-foreground-muted shrink-0 group-data-[collapsed=true]:hidden" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-60 bg-surface border-border text-foreground z-[100]">
                  <DropdownMenuLabel className="text-xs text-foreground-muted font-normal uppercase tracking-wider">
                    Minhas Contas
                  </DropdownMenuLabel>
                  <div className="max-h-[240px] overflow-y-auto custom-scrollbar">
                    {myAccounts.map((account) => (
                      <DropdownMenuItem
                        key={account.id}
                        className="gap-2 cursor-pointer focus:bg-surface-hover focus:text-foreground-strong"
                        onSelect={() => handleAccountSwitch(account.id)}
                      >
                        <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-white uppercase leading-none">
                            {account.companyName.charAt(0)}
                          </span>
                        </div>
                        <div className="flex flex-col flex-1 overflow-hidden">
                          <span className="text-sm truncate">
                            {account.companyName}
                          </span>
                          <span className="text-[10px] text-foreground-muted">
                            {account.role === "ROLE_ADMIN"
                              ? "Administrador"
                              : "Agente"}
                          </span>
                        </div>
                        {account.isCurrent && (
                          <Check className="ml-auto w-4 h-4 text-cyan-500 shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              /* ═══ REGULAR USER: Single Account Static Header ═══ */
              <div className="w-full flex items-center gap-3 p-2 rounded-lg bg-surface border border-border group-data-[collapsed=true]:w-10 group-data-[collapsed=true]:h-10 group-data-[collapsed=true]:mx-auto group-data-[collapsed=true]:justify-center group-data-[collapsed=true]:p-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-border bg-surface-active text-foreground-strong">
                  <span className="font-bold text-xs">
                    {ownCompanyName.charAt(0)}
                  </span>
                </div>
                <div className="flex flex-col items-start overflow-hidden text-left group-data-[collapsed=true]:hidden">
                  <span className="font-medium text-sm truncate w-full text-foreground">
                    {ownCompanyName}
                  </span>
                  <span className="text-[10px] text-foreground-muted truncate w-full">
                    {maxRoleLevel >= 50
                      ? "Administrador"
                      : maxRoleLevel >= 30
                        ? "Supervisor"
                        : maxRoleLevel >= 10
                          ? "Agente"
                          : "Visualizador"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Global Navigation — SUPERADMIN ONLY, no company selected */}
          {isSuper && !selectedCompanyId && (
            <>
              <SidebarItem
                icon={LayoutDashboard}
                lottie="dashboard"
                label="Dashboard"
                active={
                  location.pathname === `${basePath}/dashboard` ||
                  location.pathname === "/" ||
                  location.pathname === basePath
                }
                onClick={() => navigate(`${basePath}/dashboard`)}
              />
              <SidebarItem
                icon={Handshake}
                lottie="handshake"
                label="Parceiros"
                active={
                  location.pathname === PLATFORM_ROUTES.partners ||
                  location.pathname.startsWith(`${PLATFORM_ROUTES.partners}/`)
                }
                onClick={() => navigate(PLATFORM_ROUTES.partners)}
              />
              {/* Turmas do programa: criar e gerir turmas não depende de conta,
                  então o superadmin precisa da entrada aqui, sem escolher uma. */}
              <SidebarItem
                icon={GraduationCap}
                lottie="program"
                label="Arquitetura de Operação"
                active={location.pathname.startsWith(PLATFORM_ROUTES.program)}
                onClick={() => navigate(PLATFORM_ROUTES.program)}
              />
              {/* Menu da PLATAFORMA — só superadmin.
                  Estas telas operam sobre todas as contas (ou escolhem a conta
                  dentro da própria tela), por isso não carregam :clientId. As
                  telas DA CONTA — billing, auditoria — ficam sob
                  /internal/implementation/:clientId/. */}
              {isSuperAdmin() && (
                <>
                  <SidebarItem
                    icon={Shield}
                    lottie="plans"
                    label="Plataforma: Planos"
                    active={location.pathname === PLATFORM_ROUTES.plans}
                    onClick={() => navigate(PLATFORM_ROUTES.plans)}
                  />
                  <SidebarItem
                    icon={Sparkles}
                    lottie="contents"
                    label="Plataforma: Conteúdos"
                    active={location.pathname === PLATFORM_ROUTES.content}
                    onClick={() => navigate(PLATFORM_ROUTES.content)}
                  />
                  <SidebarItem
                    icon={ScrollText}
                    lottie="legal"
                    label="Plataforma: Documentos Legais"
                    active={location.pathname === PLATFORM_ROUTES.legalDocuments}
                    onClick={() => navigate(PLATFORM_ROUTES.legalDocuments)}
                  />
                  <SidebarItem
                    icon={FileCheck2}
                    lottie="deliveries"
                    label="Plataforma: Entregas"
                    active={location.pathname === PLATFORM_ROUTES.deliveryDocuments}
                    onClick={() => navigate(PLATFORM_ROUTES.deliveryDocuments)}
                  />
                  <SidebarItem
                    icon={Activity}
                    lottie="metrics"
                    label="Plataforma: Agent Metrics"
                    active={location.pathname === PLATFORM_ROUTES.agentMetrics}
                    onClick={() => navigate(PLATFORM_ROUTES.agentMetrics)}
                  />
                  <SidebarItem
                    icon={Megaphone}
                    lottie="broadcasts"
                    label="Plataforma: Comunicados"
                    active={location.pathname.startsWith(PLATFORM_ROUTES.comunicados)}
                    onClick={() => navigate(PLATFORM_ROUTES.comunicados)}
                  />
                  <SidebarItem
                    icon={ThumbsDown}
                    lottie="ratings"
                    label="Plataforma: Avaliações do Lucas"
                    active={location.pathname === PLATFORM_ROUTES.lucasFeedback}
                    onClick={() => navigate(PLATFORM_ROUTES.lucasFeedback)}
                  />
                  <SidebarItem
                    icon={CalendarClock}
                    lottie="schedule"
                    label="Plataforma: Atendimento do Lucas"
                    active={location.pathname === PLATFORM_ROUTES.lucasSchedule}
                    onClick={() => navigate(PLATFORM_ROUTES.lucasSchedule)}
                  />
                </>
              )}
            </>
          )}

          {/* Company Specific Navigation */}
          {selectedCompanyId && (
            <>
              {/* Main Navigation (filtered by role level, feature, permission, embed) */}
              {mainNavItems.map((item) => {
                const fullPath = navItemPath(item);
                const isActive =
                  item.path === ""
                    ? location.pathname === fullPath ||
                      location.pathname === fullPath + "/"
                    : location.pathname.startsWith(fullPath);

                return (
                  <SidebarItem
                    key={item.path}
                    icon={item.icon}
                    lottie={item.lottie}
                    label={item.label}
                    to={fullPath}
                    active={isActive}
                    className="mb-1"
                  />
                );
              })}

              {/* Core Features (filtered by role level, feature, permission, embed) */}
              {coreNavItems.length > 0 && (
                <>
                  <div className="my-3 mx-2 border-t border-border/50" />
                  {coreNavItems.map((item) => {
                    const fullPath = navItemPath(item);
                    const isActive = location.pathname.startsWith(fullPath);

                    return (
                      <SidebarItem
                        key={item.path}
                        icon={item.icon}
                        lottie={item.lottie}
                        label={item.label}
                        to={fullPath}
                        active={isActive}
                        className="mb-1"
                      />
                    );
                  })}
                </>
              )}

              {/* Team & User Management (filtered by role level, feature, permission, embed) */}
              {teamNavItems.length > 0 && (
                <>
                  <div className="my-3 mx-2 border-t border-border/50" />
                  {teamNavItems.map((item) => {
                    const fullPath = navItemPath(item);
                    const isActive = location.pathname.startsWith(fullPath);

                    return (
                      <SidebarItem
                        key={item.path}
                        icon={item.icon}
                        lottie={item.lottie}
                        label={item.label}
                        to={fullPath}
                        active={isActive}
                        className="mb-1"
                      />
                    );
                  })}
                  <SidebarItem
                    icon={LifeBuoy}
                    lottie="guides"
                    label="Guias de Uso"
                    to={isEmbed ? embedPath("/support") : "/internal/support"}
                    active={location.pathname.includes("/support")}
                    className="mb-1"
                  />
                </>
              )}
            </>
          )}
        </SidebarContent>

        <SidebarFooter>
          {/* Notifications & Support (above profile) */}
          <div className="flex flex-col gap-0.5 px-2 mb-2">
            <NotificationBell variant="sidebar" />
            {/* Mesmo lugar de sempre; o que mudou é o destino. Antes abria um
                painel flutuante que instanciava o próprio `useSupportChat` —
                dois estados locais sobre a MESMA conversa do servidor, que
                divergiam. Agora leva à home, onde o Lucas mora, e o painel
                flutuante deixou de existir. */}
            <SidebarItem
              icon={Headset}
              lottie="support"
              label="Suporte"
              active={lucasChat.isOpen}
              onClick={() => {
                lucasChat.open();
                navigate(accountPath(selectedCompanyId));
              }}
            />
            <div className="flex items-center justify-center group-data-[collapsed=true]:px-0 px-2 py-1">
              <ThemeToggleSimple />
            </div>
            <SidebarToggle />
          </div>

          {/* Profile Popover with Config */}
          {selectedCompanyId ? (
            <SidebarLock as={DropdownMenu}>
              <DropdownMenuTrigger asChild>
                <div className="p-2 pt-0">
                  <button className="w-full flex items-center gap-2 px-1 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover rounded-lg group-data-[collapsed=true]:!p-0 border border-transparent hover:border-border outline-none">
                    <div className="relative">
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage
                          src={user?.avatarUrl}
                          className="rounded-lg object-cover"
                        />
                        <AvatarFallback className="rounded-lg bg-cyan-600 text-white font-semibold">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <Circle
                        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${
                          user?.presenceStatus === "away"
                            ? "fill-foreground-muted text-foreground-muted"
                            : "fill-emerald-500 text-emerald-500"
                        }`}
                      />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsed=true]:hidden">
                      <span className="truncate font-semibold text-foreground">
                        {user?.fullName || "Usuário"}
                      </span>
                      <span className="truncate text-xs text-foreground-muted">
                        {user?.email || ""}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4 text-foreground-muted group-data-[collapsed=true]:hidden" />
                  </button>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="end"
                sideOffset={4}
                className="w-56 bg-surface border-border text-foreground rounded-lg p-1 mb-2 shadow-xl ml-2"
              >
                <div className="flex items-center gap-2 p-2 border-b border-border mb-1">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage
                      src={user?.avatarUrl}
                      className="rounded-lg object-cover"
                    />
                    <AvatarFallback className="rounded-lg bg-cyan-600 text-white font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold text-foreground">
                      {user?.fullName || "Usuário"}
                    </span>
                    <span className="truncate text-xs text-foreground-muted">
                      {user?.email || ""}
                    </span>
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="px-1 py-1 border-b border-border mb-1">
                  <DropdownMenuLabel className="text-xs text-foreground-muted font-normal px-2 py-1">
                    Status
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                    onSelect={() => updatePresenceStatus("online")}
                  >
                    <Circle className="h-2.5 w-2.5 fill-emerald-500 text-emerald-500 shrink-0" />
                    <span className="flex-1">Disponível</span>
                    {(!user?.presenceStatus ||
                      user?.presenceStatus === "online") && (
                      <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                    onSelect={() => updatePresenceStatus("away")}
                  >
                    <Coffee className="h-3.5 w-3.5 text-foreground-muted shrink-0" />
                    <span className="flex-1">Ausente</span>
                    {user?.presenceStatus === "away" && (
                      <Check className="h-3.5 w-3.5 text-foreground-muted shrink-0" />
                    )}
                  </DropdownMenuItem>
                </div>

                {/* EMB-13: upsell de billing self-service — mesma razão de
                    Billing acima, nunca aparece dentro do shell. */}
                {shouldShowUpgrade && !isEmbed && (
                  <>
                    <DropdownMenuItem
                      className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5 text-amber-500 focus:text-amber-500"
                      onSelect={() => navigate(companyPath("/profile?tab=account"))}
                    >
                      <Sparkles className="w-4 h-4" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border my-1" />
                  </>
                )}

                <DropdownMenuItem
                  className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                  onSelect={() => navigate(companyPath("/profile"))}
                >
                  <User className="w-4 h-4 text-foreground-muted" />
                  Meu Perfil
                </DropdownMenuItem>
                {/* EMB-13: billing é conta-do-parceiro, não do usuário embutido —
                    nunca aparece dentro do shell. */}
                {canAccessBilling && !isEmbed && (
                  <DropdownMenuItem
                    className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                    onSelect={() => navigate(billingPath(selectedCompanyId))}
                  >
                    <CreditCard className="w-4 h-4 text-foreground-muted" />
                    Billing
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                  onSelect={() => navigate(companyPath("/profile?tab=notifications"))}
                >
                  <Bell className="w-4 h-4 text-foreground-muted" />
                  Notifications
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-border my-1" />

                <DropdownMenuLabel className="text-xs text-foreground-muted font-normal px-2 py-1.5">
                  Configurações
                </DropdownMenuLabel>
                {configNavItems.map((item) => {
                  const fullPath = companyPath(item.path);
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      className="gap-2 cursor-pointer focus:bg-surface-hover rounded-md px-2 py-1.5"
                      onSelect={() => navigate(fullPath)}
                    >
                      <item.icon className="w-4 h-4 text-foreground-muted" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
                {/* EMB-13: sair da sessão embutida não faz sentido — quem
                    controla o login é o parceiro, não este menu. */}
                {!isEmbed && (
                  <>
                    <DropdownMenuSeparator className="bg-border my-1" />
                    <DropdownMenuItem
                      className="gap-2 cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300 rounded-md px-2 py-1.5"
                      onSelect={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      Sair
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </SidebarLock>
          ) : !isEmbed ? (
            <SidebarItem
              icon={LogOut}
              label="Sair"
              onClick={handleLogout}
              className="text-red-400 hover:bg-red-500/10 hover:text-red-300 mx-2 mb-2"
            />
          ) : null}
        </SidebarFooter>
      </Sidebar>
      {/* Main Content - overflow-hidden permite filhos controlarem scroll */}
      <main className="flex-1 min-w-0 overflow-hidden transition-all duration-300">
        {/* Add top padding on mobile for the mobile header */}
        <div
          className={
            selectedCompanyId || isFullBleedRoute
              ? "h-full w-full p-0 pt-14 md:pt-0 overflow-auto custom-scrollbar"
              : "h-full overflow-auto p-4 md:p-8 pt-16 md:pt-8 max-w-[1600px] mx-auto animate-in fade-in duration-500 custom-scrollbar"
          }
        >
          {isAccountReady ? (
            <Suspense fallback={<BrandLoader inline />}>
              <Outlet context={{ companies, refreshCompanies: fetchCompanies }} />
            </Suspense>
          ) : (
            <BrandLoader inline />
          )}
        </div>
      </main>
    </div>
  );
}

// Mobile Header Component
function MobileHeader({ currentCompany }) {
  const { setMobileOpen } = useSidebar();

  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-background border-b border-border flex items-center justify-between px-4 z-30 md:hidden">
      <div className="flex items-center">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 text-foreground-muted hover:text-foreground-strong hover:bg-surface-hover rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="ml-3 flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center border border-border ${currentCompany ? "bg-surface-active text-foreground-strong" : "bg-surface text-foreground-muted"}`}
          >
            {currentCompany ? (
              <span className="font-bold text-xs">
                {currentCompany.companyName.charAt(0)}
              </span>
            ) : (
              <Lock className="w-3.5 h-3.5" />
            )}
          </div>
          <span className="font-medium text-sm text-foreground truncate max-w-[200px]">
            {currentCompany ? currentCompany.companyName : "Touchworks"}
          </span>
        </div>
      </div>
      <NotificationBell />
    </header>
  );
}
